import React from 'react';

import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { AppButton } from '@/elements';

import { DOCS_BASE_DOMAIN } from '@/pages/merchant-pages/payment-widget/util';

import { animationFromTop } from '@/components/HomePage/data/animations';
import { WebPImage } from '@/components/WebPImage';

import { MediaQueryAmountsEnum } from '@/enum/mediaQueryAmounts.enum';

/**
 * Component for Accept payments section in Home page
 * @component
 * @example
 * <AcceptPaymentsSection />.
 */
const AcceptPaymentsSection: React.FC = () => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery(MediaQueryAmountsEnum.isMobile);

  const openDocs = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window?.open(`https://${DOCS_BASE_DOMAIN}`, '_blank').focus();
  };

  return (
    <Flex
      id='accept-payments'
      sx={{
        p: { base: '76px 40px 40px', lg: '113px 0' },
        color: 'app.black',
        justifyContent: 'center',
        borderBottom: '1px solid rgba(157, 157, 156, 0.75)',
      }}
    >
      <Container fontSize='xl' maxW='1440px' px='xl'>
        <Heading
          as='h1'
          color='app.pink'
          margin='auto'
          maxW={isMobile ? '200px' : '100%'}
          size={isMobile ? '2xl' : '3xl'}
          textAlign='center'
          w='100%'
        >
          {t('main.acceptPayments.title')}
        </Heading>

        <Text
          fontSize={!isMobile ? '26px' : '21px'}
          fontWeight='bold'
          m='20px auto 45px'
          maxW={isMobile ? '200px' : '100%'}
          textAlign='center'
        >
          {t('main.acceptPayments.text')}
        </Text>

        <Flex w='100%'>
          <Flex
            alignItems={{ base: 'center', lg: 'start' }}
            as={motion.div}
            direction={{ base: 'column', lg: 'row' }}
            initial='hidden'
            justify='space-between'
            mb='80px'
            viewport={{ amount: 0.2 }}
            w='100%'
            whileInView='visible'
          >
            <Box
              as={motion.div}
              borderRadius='80px'
              custom={2}
              h={{ base: 'fit-content', lg: '440px' }}
              mb='37px'
              p={{ base: '61px 0 66px 0', xl: '68px 0 79px 0' }}
              shadow='0px 4px 17px rgba(0, 0, 0, 0.25)'
              textAlign='center'
              variants={animationFromTop}
              w={{ base: '316px', lg: '290px', xl: '398px' }}
            >
              <AppButton onClick={openDocs} size='lg' variant='secondary'>
                {t('main.acceptPayments.paymentButton')}
              </AppButton>

              <Box
                fontSize={{ base: '16px', xl: '19px' }}
                m={{ base: '28px auto 30px', xl: '28px auto 40px' }}
                maxW='325px'
                p='0 19px'
                w='100%'
              >
                {t('main.acceptPayments.paymentText')}
              </Box>

              <WebPImage
                alt='remunoApi'
                commonPath='images/paymentWidget.png'
                sx={{
                  margin: 'auto',
                }}
                webpPath='images/webp/paymentWidget.webp'
              />
            </Box>
            <Box
              as={motion.div}
              borderRadius='80px'
              custom={4}
              h={{ base: 'fit-content', lg: '440px' }}
              mb='37px'
              p={{ base: '52px 0 67px 0', xl: '68px 0 79px 0' }}
              shadow='0px 4px 17px rgba(0, 0, 0, 0.25)'
              textAlign='center'
              variants={animationFromTop}
              w={{ base: '316px', lg: '290px', xl: '398px' }}
            >
              <AppButton onClick={openDocs} size='lg' variant='secondary'>
                {t('main.acceptPayments.apiButton')}
              </AppButton>

              <Box
                fontSize={{ base: '16px', xl: '19px' }}
                m='36px auto 78px'
                maxW='305px'
                p='0 19px'
                w='100%'
              >
                {t('main.acceptPayments.apiText')}
              </Box>

              <WebPImage
                alt='remunoApi'
                commonPath='images/api.png'
                sx={{
                  margin: 'auto',
                }}
                webpPath='images/webp/api.webp'
              />
            </Box>
            <Box
              as={motion.div}
              borderRadius='80px'
              custom={6}
              h={{ base: 'fit-content', lg: '440px' }}
              mb='37px'
              p={{ base: '52px 0 19px 0', xl: '68px 0 79px 0' }}
              shadow='0px 4px 17px rgba(0, 0, 0, 0.25)'
              textAlign='center'
              variants={animationFromTop}
              w={{ base: '316px', lg: '290px', xl: '398px' }}
            >
              <AppButton color='app.textGray' size='lg' variant='secondary'>
                {t('main.acceptPayments.pluginsButton')}
              </AppButton>

              <Box
                fontSize={{ base: '16px', xl: '19px' }}
                m='35px auto 10px'
                maxW='370px'
                p='0 19px'
                w='100%'
              >
                {t('main.acceptPayments.pluginsText')}
              </Box>

              <WebPImage
                alt='remunoApi'
                commonPath={
                  !isMobile ? 'images/plugin.png' : 'images/pluginMB.png'
                }
                sx={{
                  margin: { base: '25px auto 0', md: '50px auto 0' },
                }}
                webpPath={
                  !isMobile
                    ? 'images/webp/plugin.webp'
                    : 'images/webp/pluginMB.webp'
                }
              />
            </Box>
          </Flex>
        </Flex>
      </Container>
    </Flex>
  );
};

export default AcceptPaymentsSection;
